import { Injectable, OnDestroy } from '@angular/core';
import { Subject, fromEvent } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { EchoService } from '../echo.service';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { Routes } from '../../config/routes';
import { environment } from 'environments/environment';

/**
 * Organization broadcast event types
 */
export enum OrganizationEventType {
    ORGANIZATION_UPDATED = 'organization:updated',
    UNIT_CREATED = 'organization:unit:created',
    UNIT_UPDATED = 'organization:unit:updated',
    UNIT_DELETED = 'organization:unit:deleted',
    UNITS_BULK_UPDATED = 'organization:units:bulk-updated',
    FEATURE_FLAG_ATTACHED = 'organization:feature-flag:attached',
    FEATURE_FLAG_DETACHED = 'organization:feature-flag:detached',
    AMENITY_STATS_UPDATED = 'organization:amenity:stats:updated',
    TREASURY_TRANSFER_UPDATED = 'organization:treasury:transfer:updated',
    TREASURY_BALANCE_UPDATED = 'organization:treasury:balance:updated',
    TREASURY_CARD_UPDATED = 'organization:treasury:card:updated',
    TREASURY_CONNECT_STATUS_UPDATED = 'organization:treasury:connect-status:updated',
    TREASURY_FINANCIAL_ACCOUNT_READY = 'organization:treasury:financial-account:ready',
    TREASURY_BANK_ACCOUNTS_UPDATED = 'organization:treasury:bank-accounts:updated',
}

/**
 * Organization broadcast event payload
 */
export interface OrganizationBroadcastEvent {
    type: OrganizationEventType;
    organizationId: number;
    timestamp: number;
    data?: any;
}

/**
 * Service for broadcasting and listening to organization-level events
 * Uses BroadcastChannel API for cross-tab communication
 */
@Injectable({
    providedIn: 'root',
})
export class OrganizationBroadcastService implements OnDestroy
{
    private readonly CHANNEL_NAME = 'organization-events';
    private broadcastChannel: BroadcastChannel | null = null;
    private eventSubject$ = new Subject<OrganizationBroadcastEvent>();
    private jwtRefreshedSubject$ = new Subject<void>();
    private echoChannel: any = null;

    constructor(
        private echoService: EchoService,
        private _http: ApplicationHttpClient,
    ) {
        const jwt = LegFiJwtService.read();
        if (jwt?.admin || jwt?.superUser) {
            this.initializeBroadcastChannel();
            this.initializeLaravelEchoListener();
            this.initializeFeatureFlagListener();
        }
    }

    ngOnDestroy(): void {
        // Clean up Echo channel subscription
        if (this.echoChannel) {
            const jwt = LegFiJwtService.read();
            if (jwt?.orgId) {
                const channelName = `Organization.${jwt.orgId}`;
                try {
                    this.echoService.leave(channelName);
                    this.echoChannel = null;
                } catch (error) {
                    // Benign teardown race — dev-only breadcrumb.
                    if (!environment.production) {
                        console.error('OrganizationBroadcastService: Error leaving Echo channel:', error);
                    }
                }
            }
        }

        // Clean up BroadcastChannel
        if (this.broadcastChannel) {
            this.broadcastChannel.close();
            this.broadcastChannel = null;
        }

        // Complete the event subject
        this.eventSubject$.complete();
        this.jwtRefreshedSubject$.complete();
    }

    /**
     * Manual cleanup method for testing purposes
     * (Root services' ngOnDestroy is never called automatically)
     */
    public manualCleanup(): void {
        this.ngOnDestroy();
    }

    /**
     * Observable that emits when JWT has been refreshed due to feature flag changes
     * Services can subscribe to this to reinitialize when feature flags change
     */
    public onJwtRefreshed$() {
        return this.jwtRefreshedSubject$.asObservable();
    }

    /**
     * Listen to all organization events
     */
    public onEvent$() {
        return this.eventSubject$.asObservable();
    }

    /**
     * Listen to specific event type
     */
    public onEventType$(type: OrganizationEventType) {
        return this.eventSubject$.asObservable().pipe(
            filter(event => event.type === type)
        );
    }

    /**
     * Listen to events for a specific organization
     */
    public onOrganizationEvents$(organizationId: number) {
        return this.eventSubject$.asObservable().pipe(
            filter(event => event.organizationId === organizationId)
        );
    }

    /**
     * Listen to unit-related events for a specific organization
     */
    public onOrganizationUnitEvents$(organizationId: number) {
        return this.eventSubject$.asObservable().pipe(
            filter(event =>
                event.organizationId === organizationId &&
                (
                    event.type === OrganizationEventType.UNIT_CREATED ||
                    event.type === OrganizationEventType.UNIT_UPDATED ||
                    event.type === OrganizationEventType.UNIT_DELETED ||
                    event.type === OrganizationEventType.UNITS_BULK_UPDATED
                )
            )
        );
    }

    /**
     * Initialize BroadcastChannel if supported
     */
    private initializeBroadcastChannel(): void {
        // Check if BroadcastChannel is supported
        if (typeof BroadcastChannel !== 'undefined') {
            this.broadcastChannel = new BroadcastChannel(this.CHANNEL_NAME);

            // Listen to messages from other tabs
            fromEvent<MessageEvent>(this.broadcastChannel, 'message')
                .pipe(
                    map(event => event.data as OrganizationBroadcastEvent),
                    filter(event => this.isValidEvent(event))
                )
                .subscribe(event => {
                    this.eventSubject$.next(event);
                });
        }
    }

    /**
     * Initialize Laravel Echo listener for the organization channel
     */
    private initializeLaravelEchoListener(): void {
        const jwt = LegFiJwtService.read();
        if (!jwt || !jwt.orgId) {
            // Normal pre-auth lifecycle, not an error — dev-only breadcrumb.
            if (!environment.production) {
                console.warn('OrganizationBroadcastService: No JWT or orgId found, cannot initialize Echo listener');
            }
            return;
        }

        const channelName = `Organization.${jwt.orgId}`;

        try {
            this.echoChannel = this.echoService.private(channelName);

        // Listen for unit events
        this.echoChannel.listen('.unit.created', (data: any) => {
            this.handleEchoEvent(data);
        });

        this.echoChannel.listen('.unit.updated', (data: any) => {
            this.handleEchoEvent(data);
        });

        this.echoChannel.listen('.unit.deleted', (data: any) => {
            this.handleEchoEvent(data);
        });

        this.echoChannel.listen('.units.bulk-updated', (data: any) => {
            this.handleEchoEvent(data);
        });

        // Listen for feature flag events
        this.echoChannel.listen('.feature-flag.attached', (data: any) => {
            this.handleEchoEvent(data);
        });

        this.echoChannel.listen('.feature-flag.detached', (data: any) => {
            this.handleEchoEvent(data);
        });

            // Listen for amenity stats events
            this.echoChannel.listen('.amenity.stats.updated', (data: any) => {
                this.handleEchoEvent(data);
            });

            // Listen for treasury events
            this.echoChannel.listen('.treasury.transfer.updated', (data: any) => {
                this.handleEchoEvent(data);
            });

            this.echoChannel.listen('.treasury.balance.updated', (data: any) => {
                this.handleEchoEvent(data);
            });

            this.echoChannel.listen('.treasury.card.updated', (data: any) => {
                this.handleEchoEvent(data);
            });

            // SD-6865 broadcast adoption: live updates for post-KYC Treasury
            // readiness. Connect-status flips when the org's Stripe Connect
            // capability becomes active; financial-account ready fires when
            // outbound_transfers become active on the DBA.
            //
            // Note: the listener registration above is admin/superUser gated
            // (constructor check). This is acceptable because Treasury KYC and
            // Add Charge are admin-only flows in PayHOA — the only sessions
            // that would observe these broadcasts are the same sessions that
            // currently have Echo wired up.
            this.echoChannel.listen('.treasury.connect-status.updated', (data: any) => {
                this.handleEchoEvent(data);
            });

            this.echoChannel.listen('.treasury.financial-account.ready', (data: any) => {
                this.handleEchoEvent(data);
            });

            // SD-7414: post-provisioning external bank accounts added on the
            // KYC review screen fire account.external_account.* webhooks that
            // sync new UnifiedBankAccount rows; this broadcast tells the UI
            // bank-accounts list to refetch so the new account appears
            // without a manual refresh.
            this.echoChannel.listen('.treasury.bank-accounts.updated', (data: any) => {
                this.handleEchoEvent(data);
            });

        } catch (error) {
            this.reportError('OrganizationBroadcastService: Error initializing Echo listener', error);
        }
    }

    /**
     * Initialize listener for feature flag changes
     * Automatically refreshes JWT when feature flags are updated
     */
    private initializeFeatureFlagListener(): void {
        this.eventSubject$.pipe(
            filter(event =>
                event.type === OrganizationEventType.FEATURE_FLAG_ATTACHED ||
                event.type === OrganizationEventType.FEATURE_FLAG_DETACHED
            )
        ).subscribe(event => {
            // Trigger JWT refresh to get updated feature flags
            this.triggerJwtRefresh();
        });
    }

    /**
     * Trigger a lightweight API call to force JWT refresh via RefreshToken middleware
     */
    private triggerJwtRefresh(): void {
        const jwt = LegFiJwtService.read();
        if (!jwt || !jwt.orgId) {
            return;
        }

        // Make a lightweight GET request to force JWT refresh
        // Use a lightweight endpoint that all authenticated users can access
        // The RefreshToken middleware will automatically update the JWT with new feature flags
        const url = Routes.MakeLegFiCoreUrl(`/organizations/${jwt.orgId}`);
        this._http.get(url).subscribe({
            next: () => {
                // Emit event so services can react to the JWT update
                this.jwtRefreshedSubject$.next();
            },
            error: (err) => {
                this.reportError('[OrganizationBroadcast] Failed to refresh JWT', err);
            }
        });
    }

    /**
     * Hook for actionable broadcast errors. The Better Stack client-error sink
     * was removed (SD-7666), so this now emits a dev-only breadcrumb and is
     * otherwise a no-op; the !environment.production gate keeps it out of the
     * prod console (SD-7666 removed SD-7544's global console mask).
     */
    private reportError(message: string, error?: unknown): void {
        if (!environment.production) {
            console.error(message, error);
        }
    }

    /**
     * Handle events from Laravel Echo and propagate to local subscribers
     * Note: We do NOT propagate Echo events to BroadcastChannel because:
     * 1. Laravel Echo already broadcasts to ALL tabs via WebSocket
     * 2. Each tab's Echo listener will receive the event independently
     * 3. BroadcastChannel is only for manual client-side broadcasts
     */
    private handleEchoEvent(data: any): void {
        const event: OrganizationBroadcastEvent = {
            type: data.type as OrganizationEventType,
            organizationId: data.organizationId,
            timestamp: data.timestamp,
            data: data.data,
        };

        // Emit to local subscribers only
        this.eventSubject$.next(event);

        // Do NOT post to BroadcastChannel - Echo already handles cross-tab communication
    }

    /**
     * Validate event structure
     */
    private isValidEvent(event: any): event is OrganizationBroadcastEvent {
        return event
            && typeof event.type === 'string'
            && typeof event.organizationId === 'number'
            && typeof event.timestamp === 'number';
    }
}
